import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const MAX_COMPARE = 4;
const STORAGE_KEY = "hh-compare";

interface CompareContextValue {
  ids: string[];
  has: (id: string) => boolean;
  toggle: (id: string) => "added" | "removed" | "limit";
  add: (id: string) => "added" | "limit" | "exists";
  remove: (id: string) => void;
  clear: () => void;
  isFull: boolean;
  max: number;
}

const CompareCtx = createContext<CompareContextValue | null>(null);

export function CompareProvider({ children }: { children: React.ReactNode }) {
  const [ids, setIds] = useState<string[]>(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === "string").slice(0, MAX_COMPARE) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    } catch {
      /* quota / private mode — ignore */
    }
  }, [ids]);

  const has = useCallback((id: string) => ids.includes(id), [ids]);

  const add = useCallback(
    (id: string): "added" | "limit" | "exists" => {
      if (ids.includes(id)) return "exists";
      if (ids.length >= MAX_COMPARE) return "limit";
      setIds((prev) => [...prev, id]);
      return "added";
    },
    [ids],
  );

  const remove = useCallback((id: string) => {
    setIds((prev) => prev.filter((x) => x !== id));
  }, []);

  const toggle = useCallback(
    (id: string): "added" | "removed" | "limit" => {
      if (ids.includes(id)) {
        setIds((prev) => prev.filter((x) => x !== id));
        return "removed";
      }
      if (ids.length >= MAX_COMPARE) return "limit";
      setIds((prev) => [...prev, id]);
      return "added";
    },
    [ids],
  );

  const clear = useCallback(() => setIds([]), []);

  const value = useMemo<CompareContextValue>(
    () => ({ ids, has, toggle, add, remove, clear, isFull: ids.length >= MAX_COMPARE, max: MAX_COMPARE }),
    [ids, has, toggle, add, remove, clear],
  );

  return <CompareCtx.Provider value={value}>{children}</CompareCtx.Provider>;
}

export function useCompare(): CompareContextValue {
  const ctx = useContext(CompareCtx);
  if (!ctx) throw new Error("useCompare must be used inside CompareProvider");
  return ctx;
}

export { MAX_COMPARE };
